import React, { useEffect, useMemo, useState } from 'react';

export default function WorkHistory() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({ userId: '', start: '', end: '' });

  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.userId) params.append('userId', filters.userId);
      if (filters.start) params.append('start', new Date(filters.start).toISOString());
      if (filters.end) params.append('end', new Date(filters.end).toISOString());
      const res = await fetch(`api/work/gettasks${params.toString() ? `?${params}` : ''}`);
      if (!res.ok) throw new Error('Failed to load work tasks');
      const data = await res.json();
      setTasks(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const res = await fetch('api/work/getusers');
      const data = await res.json();
      setUsers(data);
    } catch (e) { console.error(e); }
  };

  const toDateInput = d => {
    const pad = n => String(n).padStart(2, '0');
    return `${ d.getFullYear() }-${ pad(d.getMonth() + 1) }-${ pad(d.getDate()) }`;
  };

  useEffect(() => {
    loadUsers();
    const now = new Date();
    setFilters(f => ({
      ...f,
      start: toDateInput(new Date(now.getFullYear(), 0, 1)),   // Jan 1 
      end: toDateInput(new Date(now.getFullYear(), 11, 31)),  // Dec 31
    }));
  }, []);

  useEffect(() => { setLoading(true); load(); }, [filters.userId, filters.start, filters.end]);

  const toCurrency = (n) => Number(n||0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  const toHours = (n) => Number(n||0).toLocaleString('en-US', { maximumFractionDigits: 2 });

  const totalHours = useMemo(() => tasks.reduce((s, t) => s + Number(t.timeWorked || 0), 0), [tasks]);
  const totalEarnings = useMemo(() => tasks.reduce((s, t) => s + Number(t.timeWorked || 0) * Number(t.wage || 0), 0), [tasks]);

  const resolveUserName = (userId) => {
    const u = users.find(x => String(x.id) === String(userId));
    return u?.name || u?.email || userId;
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div>
      <h2>Work History</h2>
      <div className="card p-3 mb-3">
        <div className="row g-2 align-items-end">
          <div className="col-md-3">
            <label className="form-label">User</label>
            <select className="form-select" value={filters.userId} onChange={e => setFilters(f => ({ ...f, userId: e.target.value }))}>
              <option value="">All users</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>{u.name || u.email || u.id}</option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">Start date</label>
            <input type="date" className="form-control" value={filters.start} onChange={e => setFilters(f => ({ ...f, start: e.target.value }))} />
          </div>
          <div className="col-md-3">
            <label className="form-label">End date</label>
            <input type="date" className="form-control" value={filters.end} onChange={e => setFilters(f => ({ ...f, end: e.target.value }))} />
          </div>
          <div className="col-md-3 d-flex gap-2">
            <button className="btn btn-secondary" onClick={() => setFilters({ userId: '', start: '', end: '' })}>Clear</button>
            <button className="btn btn-primary" onClick={() => load()}>Refresh</button>
          </div>
        </div>
      </div>

      <p>
        Total hours: <b>{toHours(totalHours)}</b> | Total earnings: <b>{toCurrency(totalEarnings)}</b>
      </p>
      <table className="table">
        <thead>
          <tr>
            <th>User</th>
            <th>Date</th>
            <th>Description</th>
            <th>Time Worked (hrs)</th>
            <th>Wage</th>
            <th>Earnings</th>
          </tr>
        </thead>
        <tbody>
          {tasks.map(t => (
            <tr key={t.id}>
              <td>{resolveUserName(t.userId)}</td>
              <td>{new Date(t.date).toLocaleDateString()}</td>
              <td>{t.description}</td>
              <td>{toHours(t.timeWorked)}</td>
              <td>{toCurrency(t.wage)}</td>
              <td>{toCurrency(Number(t.timeWorked || 0) * Number(t.wage || 0))}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
